import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { useEmailReport } from '../../../hooks/api/reports/useEmailReport';
import { useGenerateAttributionLink } from '../../../hooks/api/reports/useGenerateAttributionLink';
import { nuc } from '../../../utils';
import Button from '../../generic/Button';
import Icon, { icons, IconText } from '../../generic/Icon';
import { Menu, MenuItem } from '../../generic/Menu';
import { useDownloadReport } from '../hooks/useDownloadReport';
import { useReportType } from '../hooks/useReportType';
import { isReleaseGroupScope, type ReportsV3Params, type ReportsV3QueryParams, type ReportV3Format } from '../types';

import EmailReportDialog from './EmailReportDialog';

import styles from './GenerateReportButton.module.less';

export type GenerateReportAction = 'generateLink' | 'copyLink' | 'download' | 'email';

type Props = {
  /**
   * Whether the report is currently being generated.
   */
  isLoading: boolean;
  /**
   * Whether to show the "Generate/Copy Public Link" option.
   */
  shouldShowPublicLink: boolean;
 /**
  * Whether to show the "Email" option.
  */
  shouldShowEmail: boolean;
  /**
   * Callback to be called when an item is clicked.
   */
  onItemClick: (action: GenerateReportAction) => void;
  /**
   * The public URL for the report, if it has been generated.
   */
  publicURL?: string;
};

/**
 * Button + dropdown menu which allows the user to generate the report.
 * The dropdown items will eventually change based on the report type and format.
 */
export const GenerateReportButton: VoidFunctionComponent<Props> = ({
  isLoading,
  shouldShowPublicLink,
  shouldShowEmail,
  onItemClick,
  publicURL,
}) => {
  // When there's no public link option, there's only one possible action — render a flat button.
  if (!shouldShowPublicLink) {
    const action: GenerateReportAction = shouldShowEmail ? 'email' : 'download';
    const icon = shouldShowEmail ? icons.envelope : icons.download;
    const label = shouldShowEmail ? 'Send to Email' : 'Download Report';

    return (
      <Button
        type="primary"
        size="medium"
        onClick={() => onItemClick(action)}
        isFullWidth
        isDisabled={isLoading}
        icon={icon}
      >
        {label}
      </Button>
    );
  }

  return (
    <Menu
      menuButton={
        <Button type="primary" size="medium" onClick={_.noop} isFullWidth>
          <IconText icon={icons.chevronDown} text="Generate Report" alignIcon="right" />
        </Button>
    }
      onItemClick={(event) => onItemClick(event.value as GenerateReportAction)}
      direction="bottom"
      align="end"
  >
      {publicURL ? (
        <MenuItem className={styles.menuItem} value="copyLink">
          <Icon icon={icons.copy} /> Copy Public Link
        </MenuItem>
      ) : (
        <MenuItem className={styles.menuItem} value="generateLink" disabled={isLoading}>
          <Icon icon={icons.link} /> {isLoading ? 'Generating...' : 'Generate Public Link'}
        </MenuItem>
      )}
      {shouldShowEmail ? (
        <MenuItem className={styles.menuItem} value="email">
          <Icon icon={icons.envelope} /> Send to Email
        </MenuItem>
      ) : (
        <MenuItem className={styles.menuItem} value="download">
          <Icon icon={icons.download} /> Download Report
        </MenuItem>
      )}
    </Menu>
  );
};

// We always email PDF reports, because generating + downloading them would be too slow.
// Note that the exception to this rule is with Remediation Guidance reports, because they are typically smaller and can be downloaded quickly.
const alwaysEmailFormat: ReportV3Format = 'PDF';

/**
 * Container component which manages the state for the GenerateReport button.
 */
export const GenerateReportButtonContainer = () => {
  const reportType = useReportType();
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const [query] = useQuery<ReportsV3QueryParams>();
  const addToast = useAddToast();

  const reportFormat = query.format;
  const projectLocator =
    isReleaseGroup ? '' : nuc(`${params.project_id}$${params.revision_id}`);

  // We're using a trick here, where the query is set to `enabled: false` so that it only runs when `refetch()` is called.
  const {
    data: report,
    isFetching,
    refetch: generatePublicLink,
  } = useGenerateAttributionLink(
    projectLocator,
    reportFormat as ReportV3Format, // Safe cast, since `query.format` is guaranteed to be a validated by this point.
  );

  // This hook just neatly wraps up all the logic around the function which opens the download endpoint in a new tab.
  // In the browser this will start the download process immediately.
  const { downloadReport } = useDownloadReport(reportFormat as ReportV3Format);

  // Email report flow
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const { mutate: sendEmailReport, isPending: isEmailSending } = useEmailReport(reportFormat as ReportV3Format);

  // TODO: Implement logic for "if the project is too large, they can't download it".
  const isTooLargeToDownload = isReleaseGroup || false;
  const isDownloadingPDFsAllowed = reportType === 'remediationGuidance';
  const shouldShowEmail = isTooLargeToDownload || (reportFormat === alwaysEmailFormat && !isDownloadingPDFsAllowed);
  const shouldShowPublicLink = !isTooLargeToDownload && !isReleaseGroup && reportType !== 'remediationGuidance';

  const publicURL = report?.publicURL;

  const onItemClick = async (action: GenerateReportAction) => {
    if (action === 'generateLink' && !isFetching) {
      // Note: this is the weird part -- typically you'd just let the hook do the fetching like normal,
      // but in this case we need to call it on demand so that we can use the response data.
      const { data } = await generatePublicLink();
      if (data?.publicURL) {
        addToast({ content: 'Public link generated', type: 'success' });
      }
    }
    if (action === 'copyLink' && publicURL) {
      navigator.clipboard.writeText(publicURL);
      addToast({ content: 'Public link copied to clipboard', type: 'success' });
    }
    if (action === 'download') {
      downloadReport();
    }
    if (action === 'email') {
      setIsEmailDialogOpen(true);
    }
  };

  return (
    <>
      <GenerateReportButton
        isLoading={isFetching}
        shouldShowPublicLink={shouldShowPublicLink}
        shouldShowEmail={shouldShowEmail}
        onItemClick={onItemClick}
        publicURL={publicURL}
      />
      {isEmailDialogOpen && (
        <EmailReportDialog
          onClose={() => setIsEmailDialogOpen(false)}
          onConfirm={(emails) => {
            sendEmailReport(emails);
            setIsEmailDialogOpen(false);
          }}
          isLoading={isEmailSending}
        />
      )}
    </>
  );
};
